<script>
    import { onMount, tick } from 'svelte';
    import party from 'party-js';
    import Prose from './Prose.svelte';
    import Button from './Button.svelte';
    import { img_cdn } from './utils/image';
    import { site_origin } from './stores/globals';
    import { load_mux_player } from '$lib/utils/load_mux_player';

    export let body_html = '';
    export let autoplay_media = false;
    /** @type {null | {title: string, body: string, button_text: string, button_route: string}} */
    export let reward = null;

    let className = '';
    export { className as class };

    /** @type {HTMLDivElement} */
    let chat_wrapper;
    /** @type {Element[]} */
    let chat_nodes = [];

    let is_destroyed = false;
    let is_complete = false;
    let confetti_fired = false;
    /** @type {HTMLDivElement | null} */
    let reward_section = null;

    let current_scene_original = 'default';
    const default_scene_background =
        'linear-gradient(135deg, rgba(0,0,0,0.5), rgba(0,0,0,0.9))';
    let viewport_width = 0;
    let viewport_height = 0;

    let scene_current = '';
    let scene_prev = '';
    let scene_entering = false;
    let scene_leaving = false;
    let scene_token = 0;
    const scene_fade_duration = 450;
    let scene_timeout = null;
    let scene_raf = null;

    const build_scene_style = (scene_url) => {
        const scene_image = (scene_url || '').trim();
        if (
            scene_image &&
            (scene_image.startsWith('/') || scene_image.startsWith('http'))
        ) {
            const encoded = encodeURI(scene_image);
            return `background-image:url('${encoded}')`;
        }
        return `background:${default_scene_background}`;
    };

    let scene_current_style = build_scene_style(scene_current);
    let scene_prev_style = '';

    $: scene_current_style = build_scene_style(scene_current);
    $: scene_prev_style = scene_prev ? build_scene_style(scene_prev) : '';

    const start_scene_transition = (next_scene) => {
        const next = (next_scene || '').trim();
        if (next === scene_current) return;

        scene_prev = scene_current;
        scene_current = next;
        scene_token += 1;

        if (scene_timeout) clearTimeout(scene_timeout);
        if (scene_raf) cancelAnimationFrame(scene_raf);

        scene_entering = true;
        scene_leaving = false;

        scene_raf = requestAnimationFrame(() => {
            scene_entering = false;
            scene_leaving = true;

            scene_timeout = setTimeout(() => {
                scene_prev = '';
                scene_leaving = false;
            }, scene_fade_duration);
        });
    };

    const is_node_animated = (node) => {
        const computed_style = window.getComputedStyle(node);
        return (
            computed_style.animationName !== 'none' &&
            computed_style.animationPlayState === 'running'
        );
    };

    const init_voice_node = (node) => {
        const player = node.querySelector('mux-player');
        const button = node.querySelector('.js-play-pause');
        const player_overlay = node.querySelector('.js-player-overlay');
        const captions = node.querySelector('.js-captions');
        const toggle_captions = node.querySelector('.js-toggle-captions');
        const continue_button = node.querySelector('.js-continue');

        if (!player || !button) return;

        // Word-by-word captions reveal (text node slicing)
        let word_timer = null;
        let token_index = 0;
        let tokens = [];
        let text_node = null;
        let original_text = '';

        const clear_word_timer = () => {
            if (word_timer) {
                clearTimeout(word_timer);
                word_timer = null;
            }
        };

        let continue_btn_node = null;

        const prepare_captions = () => {
            if (!captions || captions.dataset.prepared === 'true') return;

            continue_btn_node = captions.querySelector('.js-continue');
            original_text = (captions.textContent || '')
                .replace('[Continue]', '')
                .trim();

            if (!original_text) return;

            text_node = document.createTextNode('');
            captions.textContent = '';
            captions.appendChild(text_node);
            if (continue_btn_node) {
                continue_btn_node.style.display = 'none';
                captions.appendChild(continue_btn_node);
            }

            tokens = original_text.split(/(\s+)/).filter((t) => t.length > 0);
            token_index = 0;
            captions.dataset.prepared = 'true';
        };

        const reveal_next_token = () => {
            if (!tokens.length || token_index >= tokens.length) {
                clear_word_timer();
                if (continue_btn_node) continue_btn_node.style.display = '';
                return;
            }

            const token = tokens[token_index];
            token_index += 1;

            const visible = tokens.slice(0, token_index).join('');
            if (text_node) text_node.textContent = visible;

            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth'
            });

            const trimmed = token.trim();
            let delay = 80 + Math.min(300, trimmed.length * 25);
            const last_char = trimmed.slice(-1);
            if (/[.,!?]/.test(last_char)) {
                delay += 750;
            }

            if (token_index < tokens.length) {
                word_timer = setTimeout(() => {
                    if (!player.paused) {
                        reveal_next_token();
                    }
                }, delay);
            } else {
                clear_word_timer();
                if (continue_btn_node) continue_btn_node.style.display = '';
            }
        };

        const reveal_remaining_tokens = () => {
            clear_word_timer();
            prepare_captions();
            if (!tokens.length) return;

            token_index = tokens.length;
            if (text_node) text_node.textContent = original_text;
            if (continue_btn_node) continue_btn_node.style.display = '';

            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth'
            });
        };

        const start_word_reveal = () => {
            prepare_captions();
            if (!tokens.length) return;
            if (!word_timer && token_index < tokens.length) {
                reveal_next_token();
            }
        };

        player.addEventListener('play', () => {
            if (captions) {
                captions.classList.remove('hidden');
                captions.style.display = 'block';
            }
            const players = document.querySelectorAll('mux-player');
            players.forEach((p) => {
                if (p !== player) {
                    // @ts-ignore
                    p?.pause();
                }
            });

            node.classList.add('is_playing');
            start_word_reveal();
        });

        player.addEventListener('ended', () => {
            node.classList.remove('is_playing');
            reveal_remaining_tokens();
        });

        let added_nodes = false;

        const add_nodes_untill_question_once = () => {
            continue_button?.remove();
            if (!added_nodes) add_nodes_untill_question();
            added_nodes = true;
            player.removeEventListener('ended', add_nodes_untill_question_once);
        };

        player.addEventListener('ended', add_nodes_untill_question_once);

        player.addEventListener('pause', () => {
            node.classList.remove('is_playing');
            clear_word_timer();
        });

        if (autoplay_media) {
            // @ts-ignore - mux-player extends HTMLVideoElement
            if (player.readyState > 0) {
                player.play();
            } else {
                player.addEventListener('canplay', () => {
                    player.play();
                });
            }
        }

        const toggle_play = () => {
            if (player.paused) {
                player.play();
            } else {
                player.pause();
            }
        };

        button.addEventListener('click', toggle_play);
        player_overlay.addEventListener('click', toggle_play);

        toggle_captions?.addEventListener('click', () => {
            captions.classList.toggle('hidden');
        });

        continue_button?.addEventListener('click', () => {
            continue_button.remove();
            add_nodes_untill_question_once();
        });
    };

    const init_video_node = (node) => {
        const player = node.querySelector('mux-player');
        const continue_button = node.querySelector('.js-continue');

        if (!player) return;

        player.addEventListener('play', () => {
            const players = document.querySelectorAll('mux-player');
            players.forEach((p) => {
                if (p !== player) {
                    // @ts-ignore
                    p?.pause();
                }
            });
        });

        let added_nodes = false;

        const add_nodes_untill_question_once = () => {
            continue_button?.remove();
            if (!added_nodes) add_nodes_untill_question(0);
            added_nodes = true;
            player.removeEventListener('ended', add_nodes_untill_question_once);
        };

        player.addEventListener('ended', add_nodes_untill_question_once);

        continue_button?.addEventListener('click', () => {
            continue_button.remove();
            player.pause();
            add_nodes_untill_question_once();
        });
    };

    const update_viewport = () => {
        const next_width = window.innerWidth || 1280;

        const screen_height = window.screen?.height || 0;
        const doc_height = document.documentElement?.clientHeight || 0;
        const inner_height = window.innerHeight || 0;
        const candidate_height = Math.max(
            screen_height,
            doc_height,
            inner_height,
            720
        );

        const width_changed = viewport_width !== next_width;

        viewport_width = next_width;

        // Prevent swaps from mobile chrome showing/hiding; still update on real width changes
        if (width_changed || candidate_height >= viewport_height) {
            viewport_height = candidate_height;
        }
    };

    const transform_image_src = (src) => {
        if (!src) return '';
        const trimmed = src.trim();
        if (!trimmed || trimmed === 'default') return '';
        const is_url = trimmed.startsWith('/') || trimmed.startsWith('http');
        if (!is_url) return '';
        const w = Math.max(1, viewport_width);
        const h = Math.max(1, viewport_height);
        return img_cdn(trimmed, { w, h });
    };

    /**
     * @param {(Element | HTMLElement)[]} nodes
     * @returns {Set<string>}
     */
    const apply_responsive_sources = (nodes = []) => {
        const urls = new Set();

        const process_scene_marker = (marker) => {
            const original =
                marker.dataset.sceneOriginal ||
                marker.dataset.scene ||
                'default';
            marker.dataset.sceneOriginal = original;
            const cdn_src = transform_image_src(original);
            if (cdn_src) {
                marker.dataset.scene = cdn_src;
                urls.add(cdn_src);
            }
        };

        nodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            // Scenes are emitted as their own block-level nodes
            if (node.classList.contains('js-scene-marker')) {
                process_scene_marker(node);
            }
        });

        return urls;
    };

    const preload_urls = (urls = new Set()) => {
        urls.forEach((url) => {
            if (!url) return;
            const img = new Image();
            img.src = url;
        });
    };

    const create_note = (note, correct) => {
        let template = `
            <div class="quiz-note not-prose ${correct ? 'correct' : 'wrong'}">
                <div>
                    <h4>${correct ? 'Nice!' : 'Incorrect'}</h4>
                    <p>${note}</p>
                </div>
            </div>
        `;

        let parser = new DOMParser();
        let doc = parser.parseFromString(template, 'text/html');
        return doc.body.firstChild;
    };

    const show_feedback_note = async (message, is_correct) => {
        const node = create_note(message, is_correct);
        await add_node_with_delay(node, 0);
    };

    const fire_confetti = () => {
        if (confetti_fired) return;
        confetti_fired = true;
        party.confetti(document.body, {
            count: party.variation.range(150, 500)
        });
    };

    const animate_wrong_answer = (node) => {
        if (!(node instanceof HTMLElement)) return;

        node.classList.remove('is-wrong');
        // force reflow so the animation can restart if the class was already there
        void node.offsetWidth;
        node.classList.add('is-wrong');

        node.addEventListener(
            'animationend',
            () => {
                node.classList.remove('is-wrong');
            },
            { once: true }
        );
    };

    const add_node_with_delay = (node, delay = 1000) => {
        if (is_destroyed) return Promise.resolve();
        if (node.classList.contains('js-quiz-video')) {
            delay = 0;
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                if (is_destroyed || !chat_wrapper) {
                    resolve();
                    return;
                }

                try {
                    chat_wrapper.appendChild(node);
                } catch (error) {
                    resolve();
                    return;
                }

                if (node.classList.contains('js-scene-marker')) {
                    current_scene_original =
                        node.dataset.sceneOriginal ||
                        node.dataset.scene ||
                        'default';
                    const transformed = transform_image_src(
                        current_scene_original
                    );
                    start_scene_transition(transformed || '');
                }

                function handle_animation_end() {
                    if (is_destroyed) {
                        resolve();
                        return;
                    }
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth'
                    });

                    if (node.classList.contains('js-quiz-voice')) {
                        init_voice_node(node);
                    }

                    if (node.classList.contains('js-quiz-video')) {
                        init_video_node(node);
                    }

                    // Prevent slide-in animation from re-playing later (e.g. after removing .is-wrong)
                    if (
                        node.classList.contains('quiz-question') ||
                        node.classList.contains('quiz-action')
                    ) {
                        node.classList.add('is-settled');
                    }

                    resolve();
                    node.removeEventListener(
                        'animationend',
                        handle_animation_end
                    );
                }

                if (is_node_animated(node)) {
                    node.addEventListener('animationend', handle_animation_end);
                } else {
                    handle_animation_end();
                }
            }, delay);
        });
    };

    const add_nodes_untill_question = async (lead = 1000) => {
        if (is_destroyed) return;
        if (is_complete) return;

        let stopped_on_interaction = false;
        let is_first = true;
        for (let node of chat_nodes) {
            if (is_destroyed) return;
            if (is_first) {
                await add_node_with_delay(node, lead);
                is_first = false;
            } else {
                await add_node_with_delay(node);
            }

            if (is_destroyed) return;
            chat_nodes = chat_nodes.slice(1);

            if (
                node.classList.contains('js-quiz-question') ||
                node.classList.contains('js-quiz-voice') ||
                node.classList.contains('js-quiz-video') ||
                node.classList.contains('js-quiz-button')
            ) {
                stopped_on_interaction = true;
                break;
            }
        }

        if (
            !is_destroyed &&
            chat_nodes.length === 0 &&
            !stopped_on_interaction
        ) {
            is_complete = true;
            if (reward) {
                await tick();
                fire_confetti();
                window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }
    };

    /**
     * @param {HTMLFormElement} question
     * @return {void}
     */
    function handle_rb_question_submit(question) {
        question.classList.remove('is-wrong');

        const is_correct =
            question.querySelector('input:checked[data-is_correct="true"]') !==
            null;

        if (!is_correct) {
            animate_wrong_answer(question);
            return;
        }

        question.classList.add('correct');

        /** @type {NodeListOf<HTMLInputElement>} */
        let rbs = question.querySelectorAll('.js-rb');
        rbs.forEach((rb) => {
            rb.disabled = true;
        });

        question.querySelector('.field-rb-actions')?.remove();

        const note = question.dataset['note'] || '';
        if (note) {
            show_feedback_note(note, true).then(() =>
                add_nodes_untill_question(0)
            );
        } else {
            add_nodes_untill_question(0);
        }
    }

    /**
     * @param {HTMLFormElement} question
     * @return {void}
     */
    function handle_cb_question_submit(question) {
        question.classList.remove('is-wrong');

        const is_no_wrong_answer =
            question.dataset['no_wrong_answer'] === 'true';

        /** @type {HTMLInputElement[]}*/
        let cbs = Array.from(question.querySelectorAll('.js-cb'));

        const is_correct =
            is_no_wrong_answer ||
            cbs.every((cb) => {
                return cb.checked === (cb.dataset['is_correct'] === 'true');
            });

        if (!is_correct) {
            animate_wrong_answer(question);
            return;
        }

        question.classList.add('correct');
        cbs.forEach((cb) => {
            cb.disabled = true;
        });
        question.querySelector('.field-cb-actions')?.remove();

        const note = question.dataset['note'] || '';
        if (note) {
            show_feedback_note(note, true).then(() =>
                add_nodes_untill_question(0)
            );
        } else {
            add_nodes_untill_question(0);
        }
    }

    const virtual_keyboard_button_handler = (e) => {
        let button = e.target;
        let field = button
            .closest('.js-question-cbuttons')
            .querySelector('input[type="text"]');
        if (field) {
            field.value += button.textContent;
        }
    };

    const virtual_keyboard_delete_handler = (e) => {
        let button = e.target;
        let field = button
            .closest('.js-question-cbuttons')
            .querySelector('input[type="text"]');

        if (field) {
            field.value = field.value.slice(0, -1);
        }
    };

    const answer_checker = ({ question_text, answer_text }) => {
        return question_text
            .split(',')
            .map((a) => a.trim().toLowerCase())
            .includes(answer_text.toLowerCase());
    };

    const custom_keys_submit_handler = (e) => {
        let form = e.target;
        if (form instanceof HTMLElement) {
            form.classList.remove('is-wrong');
        }
        let field = form.querySelector('input[type="text"]');
        let answer = field.value.trim();

        if (!answer) return;

        const is_correct = answer_checker({
            question_text: form.dataset['question_answer'],
            answer_text: answer
        });

        if (!is_correct) {
            animate_wrong_answer(form);
            field.value = '';
            return;
        }

        form.classList.add('answered');
        field.disabled = true;

        const buttons = form.querySelectorAll('button');
        buttons.forEach((b) => {
            b.disabled = true;
        });

        const note = form.dataset['note'] || '';
        if (note) {
            show_feedback_note(note, true).then(() =>
                add_nodes_untill_question(0)
            );
        } else {
            add_nodes_untill_question(0);
        }
    };

    const handle_fallacy = (e) => {
        const action_button = e.target;
        if (!(action_button instanceof HTMLElement)) return;
        const quiz_actions = action_button.parentNode;
        if (!(quiz_actions instanceof HTMLElement)) return;
        quiz_actions.classList.remove('is-wrong');

        const note = quiz_actions.dataset.note || '';
        const correct_answer = quiz_actions.dataset.correct_answer;
        const answer = action_button.dataset.value;
        const is_correct = correct_answer === answer;

        if (!is_correct) {
            animate_wrong_answer(quiz_actions);
            return;
        }

        quiz_actions.remove();
        if (note) {
            show_feedback_note(note, true).then(() =>
                add_nodes_untill_question(0)
            );
        } else {
            add_nodes_untill_question(0);
        }
    };

    const handle_button_group_action = (e) => {
        const button = e.target;
        const quiz_actions = button.parentNode;
        if (quiz_actions instanceof HTMLElement) {
            quiz_actions.classList.remove('is-wrong');
        }
        const note = quiz_actions.dataset.note || '';
        const is_single =
            quiz_actions.querySelectorAll('.js-button-group-action').length ===
            1;
        const is_correct = is_single
            ? true
            : button.dataset.is_correct === 'true';

        if (!is_correct) {
            animate_wrong_answer(quiz_actions);
            return;
        }

        quiz_actions.remove();
        if (note) {
            show_feedback_note(note, true).then(() =>
                add_nodes_untill_question(0)
            );
        } else {
            add_nodes_untill_question(0);
        }
    };

    const handle_single_button_action = (e) => {
        const action = e.target.closest('.js-quiz-button');
        if (!action) return;

        action.remove();
        add_nodes_untill_question(0);
    };

    let mounted = false;

    let resize_timeout;

    onMount(() => {
        mounted = true;

        const handle_resize = () => {
            if (resize_timeout) clearTimeout(resize_timeout);
            resize_timeout = setTimeout(() => {
                update_viewport();
                const nodes = [...chat_nodes, ...chat_wrapper.children];
                const urls = apply_responsive_sources(nodes);
                preload_urls(urls);
                if (current_scene_original) {
                    const transformed = transform_image_src(
                        current_scene_original
                    );
                    scene_current = transformed || '';
                }
            }, 180);
        };

        (async () => {
            await load_mux_player();
            await tick();
            if (is_destroyed || !chat_wrapper) return;

            chat_nodes = [...chat_wrapper.children];
            chat_wrapper.innerHTML = '';
            chat_wrapper.classList.remove('hidden');

            update_viewport();
            preload_urls(apply_responsive_sources(chat_nodes));

            add_nodes_untill_question(0);

            chat_wrapper.addEventListener('click', (e) => {
                const target = e.target;
                if (!(target instanceof Element)) return;

                if (target.matches('.js-action-fallacy, .js-action-valid')) {
                    handle_fallacy(e);
                }
                if (target.matches('.js-single-button-action')) {
                    handle_single_button_action(e);
                }
                if (target.matches('.js-question-cbuttons .js-button')) {
                    virtual_keyboard_button_handler(e);
                }
                if (target.matches('.js-question-cbuttons .js-delete')) {
                    virtual_keyboard_delete_handler(e);
                }
                if (target.matches('.js-button-group-action')) {
                    handle_button_group_action(e);
                }
            });

            chat_wrapper.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const target = e.target;
                if (!(target instanceof HTMLFormElement)) return;

                if (target.matches('.js-question-cbuttons')) {
                    custom_keys_submit_handler(e);
                }
                if (target.matches('.js-question-rb-group')) {
                    handle_rb_question_submit(target);
                }
                if (target.matches('.js-question-cb-group')) {
                    handle_cb_question_submit(target);
                }
            });

            window.addEventListener('resize', handle_resize);
        })();

        return () => {
            is_destroyed = true;
            window.removeEventListener('resize', handle_resize);
            if (resize_timeout) clearTimeout(resize_timeout);
            if (scene_timeout) clearTimeout(scene_timeout);
            if (scene_raf) cancelAnimationFrame(scene_raf);
        };
    });
</script>

<div class="relative min-h-screen overflow-hidden rounded-xl">
    <svg
        aria-hidden="true"
        focusable="false"
        class="hidden absolute w-0 h-0 overflow-hidden"
    >
        <symbol id="play" viewBox="0 0 24 24" class="fill-current">
            <path d="M8 5v14l11-7z" />
        </symbol>
        <symbol id="pause" viewBox="0 0 24 24" class="fill-current">
            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
        </symbol>
        <symbol id="captions" viewBox="0 0 24 24" class="fill-current">
            <rect
                x="4"
                y="6"
                width="16"
                height="12"
                rx="2"
                ry="2"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
            />
            <path
                d="M11 9H9a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2v-1H9c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1h2z"
            />
            <path
                d="M17 9h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2v-1h-2c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1h2z"
            />
        </symbol>
    </svg>

    {#if scene_prev_style}
        <div
            class="scene-layer prev {scene_leaving ? 'is-leaving' : ''}"
            style={scene_prev_style}
        />
    {/if}

    {#key scene_token}
        <div
            class="scene-layer current {scene_entering ? 'is-entering' : ''}"
            style={scene_current_style}
        />
    {/key}

    <div
        class="relative z-20 flex items-end gap-4 px-4 py-6 sm:py-8 min-h-screen"
    >
        <div class="flex-1 w-full max-w-5xl mx-auto">
            <Prose fluid lg class={className}>
                {#if body_html}
                    {#key mounted}
                        <div class="chat hidden" bind:this={chat_wrapper}>
                            {@html body_html}
                        </div>
                    {/key}
                {:else}
                    No game content found.
                {/if}
            </Prose>

            {#if is_complete && reward}
                <div
                    class="not-prose mt-10 flex justify-center"
                    bind:this={reward_section}
                >
                    <div class="reward-block">
                        <h3 class="reward-title">{reward.title}</h3>
                        <p class="reward-body">{reward.body}</p>
                        <div class="reward-actions">
                            <Button
                                primary
                                lg
                                on:click={() =>
                                    window.open(
                                        `${site_origin}${reward.button_route}`,
                                        '_self'
                                    )}>{reward.button_text}</Button
                            >
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    :root {
        --chat-gap: calc(var(--spacing) * 8);

        --message-radius: var(--radius-2xl);
        --message-max-width: 80%;

        --narrator-bg: var(--color-slate-800);
        --narrator-border-radius: var(--message-radius);
        --narrator-p-x: calc(var(--spacing) * 4);
        --narrator-p-y: calc(var(--spacing) * 4);

        --question-bg: var(--color-black);

        --message-bg: var(--color-neutral-800);
        --messsage-you-bg: var(--color-blue-900);
    }

    .chat :global(.js-continue) {
        display: none !important;
    }

    .scene-layer {
        position: fixed;
        inset: 0;
        z-index: 0;
        opacity: 0.95;
        background-size: cover;
        background-position: center;
        transition: opacity 450ms ease, filter 450ms ease;
        filter: blur(0px);
        will-change: opacity, filter, transform;
    }

    .scene-layer.is-entering {
        opacity: 0;
        filter: blur(6px);
    }

    .scene-layer.prev.is-leaving {
        opacity: 0;
        filter: blur(6px);
    }

    @media (prefers-reduced-motion: reduce) {
        .scene-layer {
            transition: none !important;
            filter: none !important;
        }
    }

    .chat :global(> p),
    .chat :global(> table) {
        width: fit-content;
        max-width: var(--message-max-width);
        animation: slideIn 0.3s ease-out;
        transform-origin: bottom left;

        background: var(--narrator-bg);
        border-radius: var(--narrator-border-radius);

        padding: var(--narrator-p-y) var(--narrator-p-x);

        margin-bottom: var(--chat-gap);
        margin-top: 0;
    }

    .chat :global(> :is(p + p, table + p, p + table)) {
        margin-top: calc(var(--chat-gap) * -0.75);
    }

    .chat :global(> table) {
        width: auto;
    }
    .chat :global(> table th),
    .chat :global(> table td) {
        padding-inline: calc(var(--spacing) * 3);
        padding-block: calc(var(--spacing) * 3);
    }

    .chat :global(img) {
        margin-block: calc(var(--spacing) * 3);
        max-height: 640px;
    }

    .chat :global(.quiz-question),
    .chat :global(.quiz-action) {
        transform-origin: bottom center;
        box-shadow: 0 0 0 1px var(--color-slate-800);

        width: 100%;
        max-width: 480px;
        margin-left: auto !important;
        margin-right: auto !important;
        align-self: center;
        justify-self: center;

        background: var(--question-bg);
        padding: 8px;

        align-items: center;
        justify-content: center;
        display: flex;

        padding: var(--narrator-p-y) var(--narrator-p-x);
        background: var(--question-bg);
        border-radius: var(--narrator-border-radius);

        transition: box-shadow 0.5s ease-in-out;
    }

    .chat :global(.quiz-question:not(.is-settled):not(.is-wrong)),
    .chat :global(.quiz-action:not(.is-settled):not(.is-wrong)) {
        animation: slideIn 0.3s ease-out;
    }

    .chat :global(.quiz-question.is-wrong) {
        box-shadow: 0 0 0 1px var(--color-red-500);
        transition: none;
        animation: quiz_note_shake 0.4s ease-out;
        transform-origin: center;
        will-change: transform, opacity;
    }

    .chat :global(.quiz-character-message) {
        animation: slideIn 0.3s ease-out;
        transform-origin: bottom left;
        padding-bottom: var(--chat-gap);
        margin-block: 0;
        width: fit-content;
        max-width: var(--message-max-width);
    }
    .chat :global(.quiz-character-you),
    .chat :global(.quiz-character-dream_you) {
        justify-self: flex-end;
        margin-left: auto;
        transform-origin: bottom right;
        --message-bg: var(--messsage-you-bg);
    }

    .chat :global(.quiz-character-message p) {
        line-height: 1.4;
    }

    .chat :global(.js-toggle-captions) {
        display: none !important;
    }

    .chat :global(.quiz-note) {
        margin-top: calc(var(--chat-gap) * 0.75);
        margin-bottom: calc(var(--chat-gap) * 1.5);
        margin-inline: auto;

        justify-self: center;
        width: 100%;
        max-width: 480px;
    }

    .chat :global(.quiz-note div) {
        animation: slideIn 0.3s ease-out;
        transform-origin: bottom center;

        font-size: 1em;
        line-height: 1.4;

        background: var(--narrator-bg);

        border-radius: var(--narrator-border-radius);
        padding: var(--narrator-p-x) var(--narrator-p-y);
    }

    .chat :global(.quiz-note.correct div) {
        box-shadow: 0 0 0 1px var(--color-green-500);
        animation: quiz_note_festive 0.7s ease-out;
    }

    .chat :global(.quiz-note.wrong div) {
        box-shadow: 0 0 0 1px var(--color-red-500);
        animation: quiz_note_shake 0.65s ease-out;
    }

    .chat :global(.quiz-note h4) {
        font-weight: 900;
    }

    .chat :global(.quiz-note.correct h4) {
        color: var(--color-green-500);
        font-size: 1.2em;
    }

    .chat :global(.quiz-note.wrong h4) {
        color: var(--color-red-500);
        font-weight: var(--font-weight-bold);
        font-size: 1.2em;
    }

    .reward-block {
        animation: quiz_note_festive 0.7s ease-out;
        background: var(--color-slate-800);
        border-radius: var(--radius-2xl);
        box-shadow: 0 0 0 4px var(--color-green-500);
        max-width: var(--container-xl);
        padding: calc(var(--spacing) * 6);
        width: 100%;
    }

    .reward-title {
        font-weight: var(--font-weight-black);
        font-size: var(--text-xl);
        line-height: var(--text-xl--line-height);
        margin: 0;

        @media (min-width: 768px) {
            font-size: var(--text-2xl);
            line-height: var(--text-2xl--line-height);
        }
    }

    .reward-body {
        line-height: 1.4;
        margin-bottom: 0;
        margin-top: calc(var(--spacing) * 2);
        opacity: 0.9;
    }

    .reward-actions {
        display: flex;
        justify-content: center;
        margin-top: calc(var(--spacing) * 6);
    }

    .chat :global(.quiz-voice) {
        padding-bottom: var(--chat-gap);
        width: 90%;
    }

    .chat :global(.quiz-voice.is_playing) {
        --play-icon: none;
        --pause-icon: inline;
    }

    .chat :global(.quiz-voice.is_playing .quiz-voice-player-hint) {
        opacity: 0;
    }

    .chat :global(.quiz-voice-player) {
        width: 100%;
        max-width: 320px;
        height: 120px;
        padding-right: 25px;

        animation: slideIn 0.3s ease-out;
        transform-origin: bottom left;

        --seek-backward-button: none;
        --seek-forward-button: none;
        --mute-button: none;
        --captions-button: none;
        --airplay-button: none;
        --pip-button: none;
        --fullscreen-button: none;
        --cast-button: none;
        --playback-rate-button: none;
        --volume-range: none;
        --duration-display: none;
        --controls-backdrop-color: transparent;
    }

    :global(.quiz-voice-player::part(top)),
    :global(.quiz-voice-player::part(bottom)),
    :global(.quiz-voice-player::part(button)),
    :global(.quiz-voice-player::part(time)),
    :global(.quiz-voice-player::part(control-bar)) {
        display: none;
    }

    .chat :global(.quiz-voice-text) {
        animation: slideIn 0.3s ease-out;
        transform-origin: top left;
        background: var(--color-black);
        font-family: var(--font-mono);
        font-size: 0.9em;
        margin-bottom: calc(var(--spacing) * 2);
        padding: calc(var(--spacing) * 4);
        position: relative;
        width: fit-content;
        max-width: 100%;
        border-radius: var(--message-radius);
    }

    .chat :global(.quiz-voice-text::before) {
        content: '';
        display: block;
        width: 8px;
        height: 12px;
        background: var(--color-black);
        position: absolute;
        left: 20px;
        top: -12px;
    }

    .chat :global(.quiz-video) {
        padding-bottom: var(--chat-gap);
    }

    .chat :global(.js-scene-marker) {
        display: none;
    }

    .chat :global(.field-cbuttons-control),
    .chat :global(.field-cbuttons-keyboard),
    .chat :global(.field-cbuttons-actions) {
        align-self: center;
    }

    @keyframes quiz_note_festive {
        0% {
            opacity: 0;
            transform: translateY(24px) scale(0.92) rotate(-2deg);
        }
        40% {
            opacity: 1;
            transform: scale(1) translateY(-10px) scale(1.05) rotate(2deg);
        }
        70% {
            transform: scale(1) translateY(6px) scale(1.02) rotate(-1deg);
        }
        100% {
            transform: scale(1) translateY(0);
        }
    }

    @keyframes quiz_note_shake {
        0% {
            transform: translateY(0) translateX(-12px);
        }
        50% {
            transform: translateY(0) translateX(12px);
        }
        70% {
            transform: translateY(0) translateX(-9px);
        }
        90% {
            transform: translateY(0) translateX(9px);
        }
        100% {
            transform: translateY(0);
        }
    }

    @keyframes slideIn {
        0% {
            opacity: 0;
            transform: scale(0);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }
</style>
